import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { canSee } from "@/lib/permissions";

import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CalendarPage from "@/pages/CalendarPage";
import AvailabilityPage from "@/pages/AvailabilityPage";
import BookingsPage from "@/pages/BookingsPage";
import BookingDetailPage from "@/pages/BookingDetailPage";
import CRMPage from "@/pages/CRMPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceDetailPage from "@/pages/InvoiceDetailPage";
import AutomationsPage from "@/pages/AutomationsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AnalyticsDetailPage from "@/pages/AnalyticsDetailPage";
import AdminPage from "@/pages/AdminPage";
import SettingsPage from "@/pages/SettingsPage";
import InboxPage from "@/pages/InboxPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import AIChatPage from "@/pages/AIChatPage";
import EmbedBookPage from "@/pages/EmbedBookPage";
import ProfilePage from "@/pages/ProfilePage";
import TeamPage from "@/pages/TeamPage";
import TeamMemberDetailPage from "@/pages/TeamMemberDetailPage";
import PublicProfilePage from "@/pages/PublicProfilePage";
import OwnerSignupPage from "@/pages/OwnerSignupPage";
import AcceptInvitationPage from "@/pages/AcceptInvitationPage";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-background">
                <div className="text-sm text-muted-foreground">Loading Musook…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
    return children;
}

function SectionRoute({ section, children }) {
    const { user } = useAuth();
    if (!canSee(user, section)) {
        return (
            <div className="mx-auto flex min-h-[50vh] max-w-xl flex-col items-center justify-center gap-3 px-6 text-center">
                <h1 className="text-xl font-semibold">This workspace view is not available</h1>
                <p className="text-sm text-muted-foreground">Your current role, permissions, and assignments do not include this area.</p>
            </div>
        );
    }
    return children;
}

function AppRouter() {
    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<OwnerSignupPage />} />
            <Route path="/onboarding" element={<OwnerSignupPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/book" element={<PublicBookingPage />} />
            <Route path="/embed/book" element={<EmbedBookPage />} />
            <Route path="/profile/:slug" element={<PublicProfilePage />} />
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <AppShell />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="calendar" element={<SectionRoute section="calendar"><CalendarPage /></SectionRoute>} />
                <Route path="availability" element={<SectionRoute section="availability"><AvailabilityPage /></SectionRoute>} />
                <Route path="bookings" element={<SectionRoute section="bookings"><BookingsPage /></SectionRoute>} />
                <Route path="bookings/:id" element={<SectionRoute section="bookings"><BookingDetailPage /></SectionRoute>} />
                <Route path="crm" element={<SectionRoute section="crm"><CRMPage /></SectionRoute>} />
                <Route path="clients/:id" element={<SectionRoute section="clients"><ClientDetailPage /></SectionRoute>} />
                <Route path="projects" element={<SectionRoute section="projects"><ProjectsPage /></SectionRoute>} />
                <Route path="projects/:id" element={<SectionRoute section="projects"><ProjectDetailPage /></SectionRoute>} />
                <Route path="invoices" element={<SectionRoute section="invoices"><InvoicesPage /></SectionRoute>} />
                <Route path="invoices/:id" element={<SectionRoute section="invoices"><InvoiceDetailPage /></SectionRoute>} />
                <Route path="automations" element={<SectionRoute section="automations"><AutomationsPage /></SectionRoute>} />
                <Route path="analytics" element={<SectionRoute section="analytics"><AnalyticsPage /></SectionRoute>} />
                <Route path="analytics/:view" element={<SectionRoute section="analytics"><AnalyticsDetailPage /></SectionRoute>} />
                <Route path="admin" element={<SectionRoute section="admin"><AdminPage /></SectionRoute>} />
                <Route path="settings" element={<SectionRoute section="admin"><SettingsPage /></SectionRoute>} />
                <Route path="inbox" element={<SectionRoute section="inbox"><InboxPage /></SectionRoute>} />
                <Route path="profile/edit" element={<ProfilePage />} />
                <Route path="team" element={<SectionRoute section="team"><TeamPage /></SectionRoute>} />
                <Route path="team/:id" element={<SectionRoute section="team"><TeamMemberDetailPage /></SectionRoute>} />
                <Route path="portal" element={<SectionRoute section="portal"><ClientPortalPage /></SectionRoute>} />
                <Route path="chat" element={<SectionRoute section="inbox"><AIChatPage /></SectionRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRouter />
                <Toaster
                    position="top-right"
                    toastOptions={{
                        classNames: {
                            toast: "bg-background text-foreground border-border shadow-lg rounded-lg",
                        },
                    }}
                />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;